import { Loadable } from '@/components/loaders';
import { clearStashedDataSource } from '@/features/dataSources/dataSourcesSlice';
import { RouteConstants } from '@/features/router/RouteConstants';
import { DataSource, DataSourceCodeConfig, DataSourceIDEnum, WORDPRESS_DATA_SOURCE_TYPE } from '@/models/dataSource';
import { useAppSelector } from '@/reducers';
import { useGetDataSourceTypesQuery } from '@/services/dataSources';
import { CreateStepper, useCreateStepper } from '@/lib/Steppers';
import { FC, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CheckInstallationStep, InstallationSuccessStep, SourceDetailsStep, TrackWebsiteActivityStep } from '../code/steps';
import { CREATE_DATA_SOURCE_PLUGIN, CREATE_DATA_SOURCE_PLUGIN_STEPS } from './constants';
import { WordPressInstallPluginStep } from './steps';

type CreateDataSourcePluginContentProps = {
  dataSourceId: DataSourceIDEnum;
  modalProps?: {
    onSuccess: (dataSource: DataSource) => void;
    onSuccessAndContinue?: (dataSource: DataSource) => void;
    closeModal: VoidFunction;
  };
};

export const CreateDataSourcePluginContent: FC<CreateDataSourcePluginContentProps> = ({ dataSourceId, modalProps }) => {
  const dispatch = useDispatch();
  const { steps, currentStepId, currentStepIndex, goToNextStep, goToPreviousStep, completeCurrentStep } = useCreateStepper(CREATE_DATA_SOURCE_PLUGIN_STEPS);
  const history = useHistory();

  const { isFetching, data: sources } = useGetDataSourceTypesQuery();
  const dataSourceType =
    sources?.find((source) => source.id === dataSourceId) ?? (dataSourceId === DataSourceIDEnum.WordPress ? WORDPRESS_DATA_SOURCE_TYPE : undefined);
  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCodeConfig>> | undefined;

  useEffect(() => {
    dispatch(clearStashedDataSource());
  }, []);

  const handleNextStep = () => {
    completeCurrentStep();
    goToNextStep();
  };

  const handlePrevStep = () => {
    goToPreviousStep();
  };

  const handleCancel = () => {
    if (modalProps) {
      modalProps.closeModal();
    } else {
      history.push(`${RouteConstants.dataSources}?catalog`);
    }
  };

  const handleCloseAndContinue = () => {
    if (modalProps && dataSource) {
      if (modalProps.onSuccessAndContinue) {
        modalProps.onSuccessAndContinue(dataSource as DataSource);
      } else {
        modalProps.onSuccess(dataSource as DataSource);
      }
    } else {
      history.push(RouteConstants.audiencesCreate);
    }
  };

  const handleCloseOnly = () => {
    if (modalProps && dataSource) {
      modalProps.onSuccess(dataSource as DataSource);
    } else {
      history.push(RouteConstants.dataSources);
    }
  };

  const stepTemplate = () => {
    switch (currentStepId) {
      case CREATE_DATA_SOURCE_PLUGIN.SOURCE_DETAILS:
        return dataSourceType ? (
          <SourceDetailsStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} dataSourceType={dataSourceType} />
        ) : null;

      case CREATE_DATA_SOURCE_PLUGIN.TRACK_ACTIVITY:
        return <TrackWebsiteActivityStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_PLUGIN.INSTALL_PLUGIN:
        return dataSourceType ? <WordPressInstallPluginStep dataSourceType={dataSourceType} onNextStep={handleNextStep} onPrevStep={handlePrevStep} /> : null;

      case CREATE_DATA_SOURCE_PLUGIN.CHECK_INSTALLATION:
        return <CheckInstallationStep onNextStep={handleNextStep} onPrevStep={handlePrevStep} onCancel={handleCancel} />;

      case CREATE_DATA_SOURCE_PLUGIN.INSTALLATION_SUCCESS:
        return <InstallationSuccessStep onInit={completeCurrentStep} onNextStep={handleCloseAndContinue} onPrevStep={handleCloseOnly} isModal={!!modalProps} />;

      default:
        return null;
    }
  };

  return (
    <>
      <CreateStepper steps={steps} currentStepIndex={currentStepIndex} />

      <Loadable isLoading={isFetching}>{stepTemplate()}</Loadable>
    </>
  );
};
